import React, { useEffect, useState } from 'react';
import { SaveIcon, XIcon, CalendarIcon, CheckIcon, InfoIcon } from 'lucide-react';
import { Catalog, CatalogSchema, CatalogProduct, CatalogField, DataType } from '../CatalogsManagement';
interface ProductFormProps {
  product: CatalogProduct | null;
  catalog: Catalog;
  schema: CatalogSchema | null;
  onSave: (productData: Partial<CatalogProduct>) => void;
  onCancel: () => void;
  locale: string;
}
export function ProductForm({
  product,
  catalog,
  schema,
  onSave,
  onCancel,
  locale
}: ProductFormProps) {
  // Form state
  const [formData, setFormData] = useState<Partial<CatalogProduct>>({
    name: '',
    vendorSku: '',
    price: 0,
    currency: 'USD',
    availableStock: 0,
    manufacturer: '',
    category: '',
    hsCode: '',
    warranty: '',
    outerPackQty: 1,
    outerPackUnit: 'box',
    innerPackQty: 1,
    innerPackUnit: 'unit',
    backordered: 0,
    qtyOrdered: 0,
    qtyInvoiced: 0,
    activeFrom: new Date().toISOString().split('T')[0],
    activeTo: null,
    isActive: true,
    customAttributes: {}
  });
  // Available currencies
  const currencies = ['USD', 'EUR', 'GBP', 'CAD', 'AUD', 'JPY'];
  // Common packaging units
  const packagingUnits = ['unit', 'box', 'case', 'pallet', 'pack', 'set', 'roll', 'sheet', 'bottle', 'carton'];
  // Initialize form with product data if editing
  useEffect(() => {
    if (product) {
      setFormData({
        ...product
      });
    } else {
      // Initialize custom attributes with default values from schema
      const defaultCustomAttributes: Record<string, any> = {};
      if (schema) {
        schema.fields.forEach(field => {
          defaultCustomAttributes[field.key] = field.defaultValue !== undefined ? field.defaultValue : getDefaultValueForType(field.dataType);
        });
      }
      setFormData(prev => ({
        ...prev,
        customAttributes: defaultCustomAttributes
      }));
    }
  }, [product, schema]);
  // Get default value for a data type
  const getDefaultValueForType = (dataType: DataType): any => {
    switch (dataType) {
      case 'string':
      case 'url':
        return '';
      case 'number':
      case 'currency':
        return 0;
      case 'boolean':
        return false;
      case 'date':
        return new Date().toISOString().split('T')[0];
      case 'dropdown':
        return '';
      default:
        return null;
    }
  };
  // Handle form field changes
  const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>) => {
    const {
      name,
      value,
      type
    } = e.target;
    // Handle checkbox
    if (type === 'checkbox') {
      const checked = (e.target as HTMLInputElement).checked;
      setFormData(prev => ({
        ...prev,
        [name]: checked
      }));
      return;
    }
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
  };
  // Handle number inputs
  const handleNumberChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const {
      name,
      value
    } = e.target;
    // Remove non-numeric characters except decimal point
    const sanitizedValue = value.replace(/[^\d.]/g, '');
    const numberValue = sanitizedValue === '' ? '' : parseFloat(sanitizedValue);
    if (!isNaN(numberValue as number)) {
      setFormData(prev => ({
        ...prev,
        [name]: numberValue
      }));
    }
  };
  // Handle custom attribute changes
  const handleCustomAttributeChange = (key: string, value: any, dataType: DataType) => {
    // Convert value based on data type
    let processedValue: any = value;
    if (dataType === 'number' && value !== '') {
      processedValue = Number(value);
    } else if (dataType === 'boolean') {
      processedValue = value === 'true' || value === true;
    }
    setFormData(prev => ({
      ...prev,
      customAttributes: {
        ...prev.customAttributes,
        [key]: processedValue
      }
    }));
  };
  // Handle form submission
  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    onSave(formData);
  };
  // Render input field based on data type
  const renderFieldInput = (field: CatalogField) => {
    const value = formData.customAttributes?.[field.key] ?? getDefaultValueForType(field.dataType);
    switch (field.dataType) {
      case 'string':
        return <input type="text" id={`custom-${field.key}`} name={`custom-${field.key}`} className="shadow-sm focus:ring-indigo-500 focus:border-indigo-500 block w-full sm:text-sm border-gray-300 rounded-md" value={value} onChange={e => handleCustomAttributeChange(field.key, e.target.value, field.dataType)} required={field.isRequired} data-id="element-13508" />;
      case 'number':
        return <input type="number" id={`custom-${field.key}`} name={`custom-${field.key}`} className="shadow-sm focus:ring-indigo-500 focus:border-indigo-500 block w-full sm:text-sm border-gray-300 rounded-md" value={value} onChange={e => handleCustomAttributeChange(field.key, e.target.value, field.dataType)} required={field.isRequired} step="any" data-id="element-13509" />;
      case 'boolean':
        return <select id={`custom-${field.key}`} name={`custom-${field.key}`} className="shadow-sm focus:ring-indigo-500 focus:border-indigo-500 block w-full sm:text-sm border-gray-300 rounded-md" value={value.toString()} onChange={e => handleCustomAttributeChange(field.key, e.target.value, field.dataType)} required={field.isRequired} data-id="element-13510">
            <option value="true" data-id="element-13511">Yes</option>
            <option value="false" data-id="element-13512">No</option>
          </select>;
      case 'date':
        return <input type="date" id={`custom-${field.key}`} name={`custom-${field.key}`} className="shadow-sm focus:ring-indigo-500 focus:border-indigo-500 block w-full sm:text-sm border-gray-300 rounded-md" value={value} onChange={e => handleCustomAttributeChange(field.key, e.target.value, field.dataType)} required={field.isRequired} data-id="element-13513" />;
      case 'dropdown':
        return <select id={`custom-${field.key}`} name={`custom-${field.key}`} className="shadow-sm focus:ring-indigo-500 focus:border-indigo-500 block w-full sm:text-sm border-gray-300 rounded-md" value={value} onChange={e => handleCustomAttributeChange(field.key, e.target.value, field.dataType)} required={field.isRequired} data-id="element-13514">
            <option value="" data-id="element-13515">Select an option</option>
            {field.options?.map(option => <option key={option} value={option} data-id="element-13516">
                {option}
              </option>)}
          </select>;
      case 'currency':
        return <div className="mt-1 relative rounded-md shadow-sm" data-id="element-13517">
            <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none" data-id="element-13518">
              <span className="text-gray-500 sm:text-sm" data-id="element-13519">$</span>
            </div>
            <input type="text" id={`custom-${field.key}`} name={`custom-${field.key}`} className="focus:ring-indigo-500 focus:border-indigo-500 block w-full pl-7 pr-12 sm:text-sm border-gray-300 rounded-md" value={value} onChange={e => handleCustomAttributeChange(field.key, e.target.value, field.dataType)} required={field.isRequired} pattern="^\d*(\.\d{0,2})?$" placeholder="0.00" data-id="element-13520" />
            <div className="absolute inset-y-0 right-0 pr-3 flex items-center pointer-events-none" data-id="element-13521">
              <span className="text-gray-500 sm:text-sm" data-id="element-13522">USD</span>
            </div>
          </div>;
      case 'url':
        return <input type="url" id={`custom-${field.key}`} name={`custom-${field.key}`} className="shadow-sm focus:ring-indigo-500 focus:border-indigo-500 block w-full sm:text-sm border-gray-300 rounded-md" value={value} onChange={e => handleCustomAttributeChange(field.key, e.target.value, field.dataType)} required={field.isRequired} placeholder="https://example.com" data-id="element-13523" />;
      default:
        return <input type="text" id={`custom-${field.key}`} name={`custom-${field.key}`} className="shadow-sm focus:ring-indigo-500 focus:border-indigo-500 block w-full sm:text-sm border-gray-300 rounded-md" value={value} onChange={e => handleCustomAttributeChange(field.key, e.target.value, 'string')} required={field.isRequired} data-id="element-13524" />;
    }
  };
  return <div className="bg-white shadow overflow-hidden sm:rounded-lg" data-id="element-13525">
      <div className="px-4 py-5 sm:px-6 flex justify-between items-center" data-id="element-13526">
        <h3 className="text-lg leading-6 font-medium text-gray-900" data-id="element-13527">
          {product ? 'Edit Product' : 'Add Product'}
        </h3>
        <button onClick={onCancel} className="inline-flex items-center p-1.5 border border-transparent rounded-full shadow-sm text-gray-500 hover:bg-gray-100 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500" data-id="element-13528">
          <XIcon className="h-5 w-5" data-id="element-13529" />
        </button>
      </div>
      <form onSubmit={handleSubmit} data-id="element-13530">
        <div className="border-t border-gray-200 px-4 py-5 sm:p-6" data-id="element-13531">
          <div className="grid grid-cols-1 gap-y-6 gap-x-4 sm:grid-cols-6" data-id="element-13532">
            {/* Core Product Information */}
            <div className="sm:col-span-6" data-id="element-13533">
              <h4 className="text-base font-medium text-gray-900 mb-4" data-id="element-13534">
                Basic Information
              </h4>
            </div>
            {/* Product Name */}
            <div className="sm:col-span-4" data-id="element-13535">
              <label htmlFor="name" className="block text-sm font-medium text-gray-700" data-id="element-13536">
                Product Name
              </label>
              <div className="mt-1" data-id="element-13537">
                <input type="text" name="name" id="name" className="shadow-sm focus:ring-indigo-500 focus:border-indigo-500 block w-full sm:text-sm border-gray-300 rounded-md" value={formData.name} onChange={handleChange} required data-id="element-13538" />
              </div>
            </div>
            {/* Vendor SKU */}
            <div className="sm:col-span-2" data-id="element-13539">
              <label htmlFor="vendorSku" className="block text-sm font-medium text-gray-700" data-id="element-13540">
                Vendor SKU
              </label>
              <div className="mt-1" data-id="element-13541">
                <input type="text" name="vendorSku" id="vendorSku" className="shadow-sm focus:ring-indigo-500 focus:border-indigo-500 block w-full sm:text-sm border-gray-300 rounded-md" value={formData.vendorSku} onChange={handleChange} required data-id="element-13542" />
              </div>
            </div>
            {/* Price */}
            <div className="sm:col-span-2" data-id="element-13543">
              <label htmlFor="price" className="block text-sm font-medium text-gray-700" data-id="element-13544">
                Price
              </label>
              <div className="mt-1 relative rounded-md shadow-sm" data-id="element-13545">
                <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none" data-id="element-13546">
                  <span className="text-gray-500 sm:text-sm" data-id="element-13547">$</span>
                </div>
                <input type="text" name="price" id="price" className="pl-7 shadow-sm focus:ring-indigo-500 focus:border-indigo-500 block w-full sm:text-sm border-gray-300 rounded-md" value={formData.price} onChange={handleNumberChange} required pattern="^\d*(\.\d{0,2})?$" placeholder="0.00" data-id="element-13548" />
              </div>
            </div>
            {/* Currency */}
            <div className="sm:col-span-2" data-id="element-13549">
              <label htmlFor="currency" className="block text-sm font-medium text-gray-700" data-id="element-13550">
                Currency
              </label>
              <div className="mt-1" data-id="element-13551">
                <select id="currency" name="currency" className="shadow-sm focus:ring-indigo-500 focus:border-indigo-500 block w-full sm:text-sm border-gray-300 rounded-md" value={formData.currency} onChange={handleChange} required data-id="element-13552">
                  {currencies.map(currency => <option key={currency} value={currency} data-id="element-13553">
                      {currency}
                    </option>)}
                </select>
              </div>
            </div>
            {/* Available Stock */}
            <div className="sm:col-span-2" data-id="element-13554">
              <label htmlFor="availableStock" className="block text-sm font-medium text-gray-700" data-id="element-13555">
                Available Stock
              </label>
              <div className="mt-1" data-id="element-13556">
                <input type="number" name="availableStock" id="availableStock" className="shadow-sm focus:ring-indigo-500 focus:border-indigo-500 block w-full sm:text-sm border-gray-300 rounded-md" value={formData.availableStock} onChange={handleChange} required min="0" data-id="element-13557" />
              </div>
            </div>
            {/* Manufacturer */}
            <div className="sm:col-span-3" data-id="element-13558">
              <label htmlFor="manufacturer" className="block text-sm font-medium text-gray-700" data-id="element-13559">
                Manufacturer
              </label>
              <div className="mt-1" data-id="element-13560">
                <input type="text" name="manufacturer" id="manufacturer" className="shadow-sm focus:ring-indigo-500 focus:border-indigo-500 block w-full sm:text-sm border-gray-300 rounded-md" value={formData.manufacturer} onChange={handleChange} required data-id="element-13561" />
              </div>
            </div>
            {/* Category */}
            <div className="sm:col-span-3" data-id="element-13562">
              <label htmlFor="category" className="block text-sm font-medium text-gray-700" data-id="element-13563">
                Category
              </label>
              <div className="mt-1" data-id="element-13564">
                <input type="text" name="category" id="category" className="shadow-sm focus:ring-indigo-500 focus:border-indigo-500 block w-full sm:text-sm border-gray-300 rounded-md" value={formData.category} onChange={handleChange} required data-id="element-13565" />
              </div>
            </div>
            {/* Additional Information */}
            <div className="sm:col-span-6 pt-4 border-t border-gray-200" data-id="element-13566">
              <h4 className="text-base font-medium text-gray-900 mb-4" data-id="element-13567">
                Additional Information
              </h4>
            </div>
            {/* HS Code */}
            <div className="sm:col-span-3" data-id="element-13568">
              <label htmlFor="hsCode" className="block text-sm font-medium text-gray-700" data-id="element-13569">
                HS Code
              </label>
              <div className="mt-1" data-id="element-13570">
                <input type="text" name="hsCode" id="hsCode" className="shadow-sm focus:ring-indigo-500 focus:border-indigo-500 block w-full sm:text-sm border-gray-300 rounded-md" value={formData.hsCode} onChange={handleChange} data-id="element-13571" />
              </div>
              <p className="mt-1 text-xs text-gray-500" data-id="element-13572">
                Harmonized System code for customs classification
              </p>
            </div>
            {/* Warranty */}
            <div className="sm:col-span-3" data-id="element-13573">
              <label htmlFor="warranty" className="block text-sm font-medium text-gray-700" data-id="element-13574">
                Warranty
              </label>
              <div className="mt-1" data-id="element-13575">
                <input type="text" name="warranty" id="warranty" className="shadow-sm focus:ring-indigo-500 focus:border-indigo-500 block w-full sm:text-sm border-gray-300 rounded-md" value={formData.warranty} onChange={handleChange} placeholder="e.g., 1 year" data-id="element-13576" />
              </div>
            </div>
            {/* Packaging Information */}
            <div className="sm:col-span-6 pt-4 border-t border-gray-200" data-id="element-13577">
              <h4 className="text-base font-medium text-gray-900 mb-4" data-id="element-13578">
                Packaging Information
              </h4>
            </div>
            {/* Outer Pack Quantity */}
            <div className="sm:col-span-1" data-id="element-13579">
              <label htmlFor="outerPackQty" className="block text-sm font-medium text-gray-700" data-id="element-13580">
                Outer Pack Qty
              </label>
              <div className="mt-1" data-id="element-13581">
                <input type="number" name="outerPackQty" id="outerPackQty" className="shadow-sm focus:ring-indigo-500 focus:border-indigo-500 block w-full sm:text-sm border-gray-300 rounded-md" value={formData.outerPackQty} onChange={handleChange} min="1" data-id="element-13582" />
              </div>
            </div>
            {/* Outer Pack Unit */}
            <div className="sm:col-span-2" data-id="element-13583">
              <label htmlFor="outerPackUnit" className="block text-sm font-medium text-gray-700" data-id="element-13584">
                Outer Pack Unit
              </label>
              <div className="mt-1" data-id="element-13585">
                <select id="outerPackUnit" name="outerPackUnit" className="shadow-sm focus:ring-indigo-500 focus:border-indigo-500 block w-full sm:text-sm border-gray-300 rounded-md" value={formData.outerPackUnit} onChange={handleChange} data-id="element-13586">
                  {packagingUnits.map(unit => <option key={unit} value={unit} data-id="element-13587">
                      {unit}
                    </option>)}
                </select>
              </div>
            </div>
            {/* Inner Pack Quantity */}
            <div className="sm:col-span-1" data-id="element-13588">
              <label htmlFor="innerPackQty" className="block text-sm font-medium text-gray-700" data-id="element-13589">
                Inner Pack Qty
              </label>
              <div className="mt-1" data-id="element-13590">
                <input type="number" name="innerPackQty" id="innerPackQty" className="shadow-sm focus:ring-indigo-500 focus:border-indigo-500 block w-full sm:text-sm border-gray-300 rounded-md" value={formData.innerPackQty} onChange={handleChange} min="1" data-id="element-13591" />
              </div>
            </div>
            {/* Inner Pack Unit */}
            <div className="sm:col-span-2" data-id="element-13592">
              <label htmlFor="innerPackUnit" className="block text-sm font-medium text-gray-700" data-id="element-13593">
                Inner Pack Unit
              </label>
              <div className="mt-1" data-id="element-13594">
                <select id="innerPackUnit" name="innerPackUnit" className="shadow-sm focus:ring-indigo-500 focus:border-indigo-500 block w-full sm:text-sm border-gray-300 rounded-md" value={formData.innerPackUnit} onChange={handleChange} data-id="element-13595">
                  {packagingUnits.map(unit => <option key={unit} value={unit} data-id="element-13596">
                      {unit}
                    </option>)}
                </select>
              </div>
            </div>
            {/* Inventory Information */}
            <div className="sm:col-span-6 pt-4 border-t border-gray-200" data-id="element-13597">
              <h4 className="text-base font-medium text-gray-900 mb-4" data-id="element-13598">
                Inventory Information
              </h4>
            </div>
            {/* Backordered */}
            <div className="sm:col-span-2" data-id="element-13599">
              <label htmlFor="backordered" className="block text-sm font-medium text-gray-700" data-id="element-13600">
                Backordered Quantity
              </label>
              <div className="mt-1" data-id="element-13601">
                <input type="number" name="backordered" id="backordered" className="shadow-sm focus:ring-indigo-500 focus:border-indigo-500 block w-full sm:text-sm border-gray-300 rounded-md" value={formData.backordered} onChange={handleChange} min="0" data-id="element-13602" />
              </div>
            </div>
            {/* Quantity Ordered */}
            <div className="sm:col-span-2" data-id="element-13603">
              <label htmlFor="qtyOrdered" className="block text-sm font-medium text-gray-700" data-id="element-13604">
                Quantity Ordered
              </label>
              <div className="mt-1" data-id="element-13605">
                <input type="number" name="qtyOrdered" id="qtyOrdered" className="shadow-sm focus:ring-indigo-500 focus:border-indigo-500 block w-full sm:text-sm border-gray-300 rounded-md" value={formData.qtyOrdered} onChange={handleChange} min="0" data-id="element-13606" />
              </div>
            </div>
            {/* Quantity Invoiced */}
            <div className="sm:col-span-2" data-id="element-13607">
              <label htmlFor="qtyInvoiced" className="block text-sm font-medium text-gray-700" data-id="element-13608">
                Quantity Invoiced
              </label>
              <div className="mt-1" data-id="element-13609">
                <input type="number" name="qtyInvoiced" id="qtyInvoiced" className="shadow-sm focus:ring-indigo-500 focus:border-indigo-500 block w-full sm:text-sm border-gray-300 rounded-md" value={formData.qtyInvoiced} onChange={handleChange} min="0" data-id="element-13610" />
              </div>
            </div>
            {/* Lifecycle Information */}
            <div className="sm:col-span-6 pt-4 border-t border-gray-200" data-id="element-13611">
              <h4 className="text-base font-medium text-gray-900 mb-4" data-id="element-13612">
                Lifecycle Information
              </h4>
            </div>
            {/* Active Status */}
            <div className="sm:col-span-6 flex items-center" data-id="element-13613">
              <div className="flex h-5 items-center" data-id="element-13614">
                <input id="isActive" name="isActive" type="checkbox" className="h-4 w-4 rounded border-gray-300 text-indigo-600 focus:ring-indigo-500" checked={formData.isActive} onChange={handleChange} data-id="element-13615" />
              </div>
              <div className="ml-3 text-sm" data-id="element-13616">
                <label htmlFor="isActive" className="font-medium text-gray-700" data-id="element-13617">
                  Active
                </label>
                <p className="text-gray-500" data-id="element-13618">
                  Make this product available for use
                </p>
              </div>
            </div>
            {/* Active From */}
            <div className="sm:col-span-3" data-id="element-13619">
              <label htmlFor="activeFrom" className="block text-sm font-medium text-gray-700" data-id="element-13620">
                Active From
              </label>
              <div className="mt-1 relative rounded-md shadow-sm" data-id="element-13621">
                <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none" data-id="element-13622">
                  <CalendarIcon className="h-5 w-5 text-gray-400" data-id="element-13623" />
                </div>
                <input type="date" name="activeFrom" id="activeFrom" className="focus:ring-indigo-500 focus:border-indigo-500 block w-full pl-10 sm:text-sm border-gray-300 rounded-md" value={formData.activeFrom} onChange={handleChange} required data-id="element-13624" />
              </div>
            </div>
            {/* Active To */}
            <div className="sm:col-span-3" data-id="element-13625">
              <label htmlFor="activeTo" className="block text-sm font-medium text-gray-700" data-id="element-13626">
                Active To
              </label>
              <div className="mt-1 relative rounded-md shadow-sm" data-id="element-13627">
                <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none" data-id="element-13628">
                  <CalendarIcon className="h-5 w-5 text-gray-400" data-id="element-13629" />
                </div>
                <input type="date" name="activeTo" id="activeTo" className="focus:ring-indigo-500 focus:border-indigo-500 block w-full pl-10 sm:text-sm border-gray-300 rounded-md" value={formData.activeTo || ''} onChange={handleChange} data-id="element-13630" />
              </div>
              <p className="mt-2 text-sm text-gray-500" data-id="element-13631">
                Leave blank for no end date
              </p>
            </div>
            {/* Custom Attributes */}
            {schema && schema.fields.length > 0 && <>
                <div className="sm:col-span-6 pt-4 border-t border-gray-200" data-id="element-13632">
                  <h4 className="text-base font-medium text-gray-900 mb-4" data-id="element-13633">
                    Custom Attributes
                  </h4>
                </div>
                {schema.fields.map(field => <div key={field.id} className="sm:col-span-3" data-id="element-13634">
                    <label htmlFor={`custom-${field.key}`} className="block text-sm font-medium text-gray-700" data-id="element-13635">
                      {field.name}
                      {field.isRequired && <span className="text-red-500" data-id="element-13636">*</span>}
                    </label>
                    <div className="mt-1" data-id="element-13637">{renderFieldInput(field)}</div>
                  </div>)}
              </>}
          </div>
        </div>
        <div className="px-4 py-3 bg-gray-50 text-right sm:px-6 flex justify-end space-x-3" data-id="element-13638">
          <button type="button" className="inline-flex justify-center py-2 px-4 border border-gray-300 shadow-sm text-sm font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500" onClick={onCancel} data-id="element-13639">
            Cancel
          </button>
          <button type="submit" className="inline-flex justify-center py-2 px-4 border border-transparent shadow-sm text-sm font-medium rounded-md text-white bg-indigo-600 hover:bg-indigo-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500" data-id="element-13640">
            <SaveIcon className="h-4 w-4 mr-2" data-id="element-13641" />
            {product ? 'Update Product' : 'Add Product'}
          </button>
        </div>
      </form>
    </div>;
}